import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Sentry } from "@/lib/sentry";

// Pin the auth-email redirect to the canonical .org host so verification
// links work no matter which host (lovable.app, dev, .org) the user
// signed up from. Override via VITE_APP_URL for local dev.
const APP_URL = import.meta.env.VITE_APP_URL || "https://fbn-entrepreneurs.org";

export interface SignupData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  fbnChapter: string;
  city?: string;
  familyBusiness?: string;
  segment?: string[];
  bio?: string;
  workingOnProject?: boolean;
  projectType?: string;
  projectDescription?: string;
  sdgGoals?: string[];
  linkedinUrl?: string;
  websiteUrl?: string;
  industryInterests?: string[];
  phoneNumber?: string;
}


export const useAuthOperations = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const signUp = async (data: SignupData) => {
    setLoading(true);
    try {
      // All signup work happens server-side in the `signup` edge function:
      // it creates the auth user via the admin API (no Supabase-sent email),
      // sends a single branded verification email via Resend, and notifies
      // admins. This replaces the old `auth.signUp` + `signup-verification`
      // pair that caused duplicate emails.
      const { data: result, error: invokeError } = await supabase.functions.invoke("signup", {
        body: {
          email: data.email,
          password: data.password,
          redirect_to: APP_URL,
          data: {
            first_name: data.firstName,
            last_name: data.lastName,
            fbn_chapter: data.fbnChapter,
            city: data.city || null,
            family_business: data.familyBusiness || null,
            segment: data.segment || [],
            bio: data.bio || null,
            working_on_project: data.workingOnProject || false,
            project_type: data.projectType || null,
            project_description: data.projectDescription || null,
            sdg_goals: data.sdgGoals || [],
            linkedin_url: data.linkedinUrl || null,
            website_url: data.websiteUrl || null,
            industry_interests: data.industryInterests || [],
            phone_number: data.phoneNumber || null,
            pending_legal_acceptance: true,
          },
        },
      });

      if (invokeError) {
        Sentry.captureException(invokeError, {
          tags: { area: "auth_signup" },
          extra: { hasEmail: !!data.email, hasChapter: !!data.fbnChapter },
        });
        throw invokeError;
      }

      if (result && typeof result === "object" && "error" in result && result.error) {
        const message =
          typeof (result as { message?: string }).message === "string"
            ? (result as { message: string }).message
            : "Signup failed. Please try again.";
        throw new Error(message);
      }

      toast({
        title: "Check your email",
        description: "We've sent you a verification link to confirm your account.",
      });

      return { success: true };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Something went wrong. Please try again.";
      toast({
        title: "Signup failed",
        description: message,
        variant: "destructive",
      });
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      toast({
        title: "You're successfully logged in",
      });

      return { success: true, user: data.user };
    } catch (error: unknown) {
      const rawMessage = error instanceof Error ? error.message : "";
      const isInvalidCreds = /invalid login credentials/i.test(rawMessage);
      const isUnconfirmed = /email not confirmed|not confirmed/i.test(rawMessage);

      let field: "email" | "password" | null = null;
      let title = "Log in failed";
      let description = rawMessage || "Invalid email or password.";

      if (isUnconfirmed) {
        field = "email";
        title = "Email not verified";
        description = "Please verify your email first. Check your inbox for the confirmation link.";
      } else if (isInvalidCreds) {
        try {
          const { data: check } = await supabase.functions.invoke("check-email-exists", {
            body: { email },
          });
          if (check && typeof check === "object" && "exists" in check) {
            if (!check.exists) {
              field = "email";
              title = "Email not found";
              description = "We couldn't find an account for this email. Please check for typos or sign up.";
            } else if (check.confirmed === false) {
              field = "email";
              title = "Email not verified";
              description = "Please verify your email first. Check your inbox for the confirmation link.";
            } else {
              field = "password";
              title = "Incorrect password";
              description = "That password doesn't match. Try again or reset your password.";
            }
          }
        } catch {
          // fall back to generic message
        }
      }

      toast({
        title,
        description,
        variant: "destructive",
      });
      return { success: false, error, field, message: description };
    } finally {
      setLoading(false);
    }
  };

  return { signUp, signIn, loading };
};
